import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import camelcaseKeys from 'camelcase-keys';
import { DesignGroupNavigationAsset } from './DesignGroupNavigation';
import { sortDesignGroupAssets } from './DesignGroupNavigation/utils';

type Params = {
  asset: any;
  groupAssets?: any[];
  assetId?: string;
  setHasMounted: Dispatch<SetStateAction<boolean>>;
};

const useDesignGroupNavigation = ({
  asset,
  groupAssets = [],
  assetId,
  setHasMounted
}: Params) => {
  const [groupNavigationCollapsed, setGroupNavigationCollapsed] = useState(
    false
  );

  const currentAsset = useMemo(
    () => camelcaseKeys(asset, { deep: true }) as DesignGroupNavigationAsset,
    [asset]
  );
  const groupNavigationAssets = useMemo(
    () =>
      sortDesignGroupAssets(
        camelcaseKeys(groupAssets, { deep: true }) as DesignGroupNavigationAsset[]
      ),
    [groupAssets]
  );
  const [selectedAsset, setSelectedAsset] = useState<
    DesignGroupNavigationAsset
  >(currentAsset);
  const showGroupNavigation = groupNavigationAssets.length > 1;
  const groupNavigationWidth = showGroupNavigation
    ? groupNavigationCollapsed
      ? 76
      : 280
    : 0;

  useEffect(() => {
    setSelectedAsset(currentAsset);
  }, [currentAsset.id]);

  useEffect(() => {
    const routeAssetId = Number(assetId);
    if (!routeAssetId || routeAssetId === selectedAsset.id) return;

    const routeAsset =
      groupNavigationAssets.find(({ id }) => id === routeAssetId) ||
      (currentAsset.id === routeAssetId ? currentAsset : null);

    if (routeAsset) setSelectedAsset(routeAsset);
  }, [assetId, currentAsset, groupNavigationAssets, selectedAsset.id]);

  useEffect(() => {
    setHasMounted(false);
    document.title = `${
      selectedAsset.fileName || selectedAsset.name || 'Design asset'
    } | BugHerd`;
  }, [selectedAsset.id, setHasMounted]);

  return {
    groupNavigationCollapsed,
    setGroupNavigationCollapsed,
    selectedAsset,
    setSelectedAsset,
    groupNavigationAssets,
    showGroupNavigation,
    groupNavigationWidth
  };
};

export default useDesignGroupNavigation;
